import {
  Box,
  Grid,
  Link,
  ListItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { m } from 'framer-motion';
import Image from 'next/legacy/image';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useRef } from 'react';

import { MotionContainer, varFade } from '@larvitu/shared/framer-motion/utils';
import {
  CarouselArrows,
  carouselDots,
  cssStyles,
} from '@larvitu/shared/mui/ui-components';

import type { BoxProps, ListItemProps } from '@mui/material';
import type Slider from 'react-slick';

interface SubLinkStyleProps extends ListItemProps {
  active: boolean;
}

interface IconBulletStyleProps extends BoxProps {
  active: boolean;
}

const SubLinkStyle = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'active',
})<SubLinkStyleProps>(({active = false, theme}) => ({
  ...theme.typography.body3,
  padding: 0,
  width: 'auto',
  cursor: 'pointer',
  color: theme.palette.text.secondary,
  transition: theme.transitions.create('color'),
  '&:hover': {color: theme.palette.text.primary},
  ...(active && {
    ...theme.typography.subtitle3,
    color: theme.palette.text.primary,
  }),
}));

const IconBulletStyle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<IconBulletStyleProps>(({active = false, theme}) => ({
  width: 12,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  '&:before': {
    content: '""',
    display: 'block',
    width: 4,
    height: 4,
    borderRadius: '50%',
    backgroundColor: theme.palette.text.disabled,
  },
  ...(active && {
    '&:before': {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      transition: theme.transitions.create('all', {
        duration: theme.transitions.duration.shortest,
      }),
    },
  }),
}));

export interface NavDesktopStyleProps {
  transparent?: boolean;
  scrolling?: boolean;
}

export const NavDesktopStyle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'transparent' && prop !== 'scrolling',
})<NavDesktopStyleProps>(({scrolling, theme}) => ({
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.default,
  position: 'relative',
  px: 2,
  py: 6,
  ...(scrolling ? {...cssStyles(theme).bgBlur()} : {}),
}));

// ----------------------------------------------------------------------

// const variants = {
//   enter: (direction: number) => (
//     {
//       x: direction > 0 ? 1000 : -1000,
//       opacity: 0,
//     }
//   ),
//   center: {
//     zIndex: 1,
//     x: 0,
//     opacity: 1,
//   },
//   exit: (direction: number) => (
//     {
//       zIndex: 0,
//       x: direction < 0 ? 1000 : -1000,
//       opacity: 0,
//     }
//   ),
// };

// ----------------------------------------------------------------------

interface LinkItemProps {
  title: string;
  href: string;
  active: boolean;
}

const LinkItem = ({title , href, active}: LinkItemProps): JSX.Element => (
  <NextLink key={title} href={href} passHref>
    <Link
      color="inherit"
      underline="hover"
      component={m.a}
      variants={
        varFade({
          distance: 12,
          durationIn: 0.16,
          durationOut: 0.12,
          easeIn: 'easeIn',
        }).inRight
      }
    >
      <SubLinkStyle active={active}>
        <IconBulletStyle active={active}/>
        {title}
      </SubLinkStyle>
    </Link>
  </NextLink>
);

// ----------------------------------------------------------------------

export default function NavDesktopMenu({
                                         lists,
                                         isScrolling,
                                       }: {
  isScrolling: boolean; lists: {
    subheader: string
    title?: string, items?: {
      title: string;
      path: string;
    }[], cover?: string, path?: string
  }[]
}): JSX.Element {
  const router = useRouter();
  const theme = useTheme();
  const lengthCheck = 5;

  const {t} = useTranslation('common');

  const carouselRef = useRef<Slider | null>(null);

  const carouselList = lists.filter((list) => list.subheader !== 'Common');

  const minList = lists.length > lengthCheck;

  const carouselSettings = {
    arrows: false,
    dots: true,
    infinite: false,
    slidesToShow: 4,
    slidesToScroll: 1,
    rtl: Boolean(theme.direction === 'rtl'),
    ...carouselDots({}),
  };

  const handlePrevious = (): void => {
    carouselRef.current?.slickPrev();
  };

  const handleNext = (): void => {
    carouselRef.current?.slickNext();
  };

  return (
    <Box
      sx={{
        width: '100vw',
        paddingTop: isScrolling ?? false ? '25px' : '20px',
        // background: 'transparent',
      }}
      component={Paper}
    >
      <Grid container columns={15} spacing={4}>
        <Grid item xs={16}>
          <NavDesktopStyle
            scrolling={isScrolling}
            sx={{
              position: 'relative',
              px: 2,
              py: 6,
            }}
          >
            <Grid container>
              {carouselList.map((list) => {
                const {subheader, title, items, cover, path} = list;

                return (
                  <Grid item md={4} key={title} component={MotionContainer}>
                    <m.div variants={varFade({distance: 80}).inLeft}>
                      <NextLink href={path ?? ''} passHref>
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            transition: theme.transitions.create('opacity'),
                            cursor: 'pointer',
                            // border: (theme) => `solid 1px ${theme.palette.divider}`,
                            boxShadow: theme.customShadows.z4,
                            '&:hover': {
                              opacity: 0.8,
                              color: 'text.secondary',
                            },
                          }}
                        >
                          {(cover ?? '') && (
                            <Box marginBottom={0} marginTop="auto">
                              <Box mb={2.5}>
                                <Image
                                  src={cover ?? ''}
                                  width={"100%" as unknown as number}
                                  height={"100%" as unknown as number}
                                  alt={title}
                                  layout="fixed"
                                  objectFit="cover"
                                  // objectPosition="0 40%"
                                  objectPosition="0 top"
                                  placeholder="blur"
                                  blurDataURL="https://zone-assets-api.vercel.app/assets/img_placeholder.svg"
                                  style={{
                                    borderRadius: 1.5,
                                    // border: `solid 1px ${theme.palette.divider}`,
                                  }}
                                />
                              </Box>
                            </Box>
                          )}
                          <Box p={3}>
                            <Typography variant="h4" component="h2">
                              {/*{i18n?.t(title, {ns: "common", defaultValue: ""} )}*/}
                              {t(title ?? '', {ns: 'common', defaultValue: ''})}
                            </Typography>
                            <Typography
                              variant="subtitle1"
                              color="text.disabled"
                            >
                              {subheader}
                            </Typography>
                          </Box>
                        </Box>
                      </NextLink>
                    </m.div>

                    <Stack spacing={1.5} alignItems="flex-start">
                      {items?.map((item) => {
                        const active =
                          router.pathname === item.path ||
                          router.asPath === item.path;

                        return (
                          <LinkItem
                            key={item.title}
                            title={item.title}
                            href={item.path}
                            active={active}
                          />
                        );
                      })}
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>

            {minList && (
              <CarouselArrows
                onNext={handleNext}
                onPrevious={handlePrevious}
                sx={{
                  top: -28,
                  position: 'relative',
                  justifyContent: 'flex-end',
                }}
              />
            )}
          </NavDesktopStyle>
        </Grid>
      </Grid>
    </Box>
  );
}
